import videojs from 'video.js';

import ModalDialog from '../../components/modal-dialog.js';

import generateShareForm from './share/share-form.js';
import { getMsFromString, enableFieldByCheckbox, padTimeBasedUponDuration, createValidationForTimeString } from '../../util/shared-control-bar.js';
import './share/share-form.css';

import { isPresentationId } from '../../util';
import { PresentationStatus } from '../../api/presentation';
import ShareIcon from '../../assets/images/share-white-24dp.svg';

const _modal = new WeakMap();
const _content = new WeakMap();
let localize;

/**
 * The Share module.
 * @member {SharePlugin} share
 * @memberof MediasitePlayer#
 */

/**
 * Configures the share button that is displayed in the control bar menu.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 */
class SharePlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        const modal = new ModalDialog(player, {
            temporary: false,
            uncloseable: false,
            pauseOnOpen: false,
            description: localize('Share Presentation'),
            displayAsPanel: true
        });
        modal.addClass('mediasite-player__share-modal');
        modal.addClass('mediasite-player__control-bar-modal');
        _modal.set(this, modal);
        player.controlbarMenu().addMenuItem(localize('Share Presentation'), { id: this.name, icon: ShareIcon, callback: () => this.openModal() });

        player.ready(() => {
            player.layout().addChild(modal);
        });
    }

    /**
     * Opens the Share modal dialog.
     * @example
     * player.share.openModal();
     */
    openModal() {
        if (isPresentationId(this.player.loader().presentationId) && this.enabled) {
            const modal = _modal.get(this);
            const content = generateShareForm(this.player);
            _content.set(this, content);

            this.setupModalContent(content);
            modal.fillWith(content);
            modal.open();
        }
    }

    /**
     * Closes the Share modal dialog.
     * @example
     * player.share.closeModal();
     */
    closeModal() {
        _modal.get(this).close();
    }

    /**
     * Updates the content of the share modal
     * @private
     * @param {HTMLDivElement} content
     * player.share.setupModalContent();
     */
    setupModalContent(content) {
        this.#hideIfLive(content.querySelector('.time-fieldset'));

        //set up the time checkbox and field the same way as the ask a question modal
        const timeCheckbox = content.querySelector('input[type=checkbox]');
        const timeField = content.querySelector('input[name=time]');

        enableFieldByCheckbox(timeField, timeCheckbox);
        timeField.value = padTimeBasedUponDuration(videojs.formatTime, this.player.currentTime(), this.player.duration());
        timeField.setAttribute("pattern", createValidationForTimeString(timeField.value));

        const presentationLinkField = content.querySelector("#share-presentation-link");

        let copyURL = new URL(`${location.protocol}//${location.host}${location.pathname}`);
        this.#updateTextarea(presentationLinkField, copyURL);

        // When timeCheckbox changes, add or remove the playfrom parameter
        timeCheckbox.addEventListener('change', event => {
            if (!timeCheckbox.checked) {
                if (copyURL.searchParams.has("playfrom")) {
                    copyURL.searchParams.delete("playfrom");
                }
                this.#updateTextarea(presentationLinkField, copyURL);
            } else {
                this.#updateTextarea(presentationLinkField, this.#addTimeValueToURL(copyURL, timeField.value));
            }
        });

        timeField.addEventListener('change', event => {
            this.#updateTextarea(presentationLinkField, this.#addTimeValueToURL(copyURL, timeField.value));
            });

        this.#copyTextareaOnClick(content.querySelector("#share-copy-link"), presentationLinkField);

        //allows the pop-up for the time field validation to work without submitting anything
        this.#disableFormSubmit(content.querySelector('form'));
        
        }

    /**
     * Depending if the video is live or not, hide the provided element.
     **/
    #hideIfLive(elementToHide) {
        let liveStatus = this.player.live?.().status;
        //if we're doing a live video, remove the ability to copy the time
        if (liveStatus && liveStatus !== PresentationStatus.OnDemand) {
            elementToHide.classList.add("vjs-hidden");
        }
    }

    /**
     * Adds the time value into the url through the playfrom parameter.
     * Updates it if there already is a playfrom parameter.
     * Removes the playfrom parameter if the value provided is invalid.
     **/
    #addTimeValueToURL(url, timeValue) {
        let convertedValue = getMsFromString(timeValue);

        //we can't have a negative time value to travel to, so remove the playfrom if one is attempted
        if (convertedValue < 0) {
            if (url.searchParams.has("playfrom")) {
                url.searchParams.delete("playfrom");
            }

            return url;
        }

        if (url.searchParams.has("playfrom")) {
            url.searchParams.set("playfrom", convertedValue);
        } else {
            url.searchParams.append("playfrom", convertedValue);
        }
        return url;
    }

    /**
     * Updates the text area with the string version of whatever value is provided.
    */
    #updateTextarea(textarea, value) {
        textarea.value = value.toString();
    }

    /**
     * Sets up an element to copy the value of the textarea to the user's clipboard when the element is clicked.
    */ 
    #copyTextareaOnClick(clickElement, textarea) {
        if (!clickElement || !textarea) {
            return;
        }
        clickElement.addEventListener('click', event => {
            textarea.select();
            navigator.clipboard.writeText(textarea.value);
        });
    }

    /**
     * Prevents the form provided from submitting anything on submit.
     */
    #disableFormSubmit(form) {
        if (!form) {
            return;
        }
        form.addEventListener('submit', event => {
            event.preventDefault();
        });
    }

    /**
     * A boolean indicating whether or not the Share feature is enabled in the UI.<br><br>
     * Note: disabling the player's Share feature will not prevent users from sharing the page or its content via default browser means (such as the location bar).
     * @type {boolean}
     * @example
     * player.share.enabled = true;
     */
    get enabled() {
        return this.player.controlbarMenu().isMenuItemEnabled(this.name);
    }

    set enabled(enabled) {
        if (enabled) {
            this.player.controlbarMenu().enableMenuItem(this.name);
        }
        else {
            this.player.controlbarMenu().disableMenuItem(this.name);
        }
    }

    dispose() {
        if (this.player?.usingPlugin('controlbarMenu')) {
            this.player.controlbarMenu().removeMenuItem(this.name);
        }

        super.dispose();
    }
}

export default SharePlugin;
